/* assets/lab-reviews.js — the customer review cards (sections/lab-reviews.liquid).
   Senja has no public JSON endpoint for a widget: its platform.js carries the reviews as a JSON
   array literal and is served with `access-control-allow-origin: *`, so the cards read that array
   and render in the theme instead of Senja's shadow-DOM widget. The format is undocumented; when
   it changes the track stays hidden, the Trustpilot summary above it still links to every
   review, and the failure goes to the console.
   The script is fetched once the section is within 800 px of the viewport, without cookies,
   and Senja's own analytics call is never made. Reviewer names are shortened to the first name
   and the initial of the last one before they reach the page. */
if (!customElements.get('lab-reviews')) {
  const TIMEOUT_MS = 10000;
  const REFRESH_MS = 10 * 60 * 1000;
  const HONORIFIC = /^(mr|mrs|ms|miss|dr|sig|sig\.ra|herr|frau|mme|mlle)\.?$/i;

  const isReview = (r) =>
    r !== null &&
    typeof r === 'object' &&
    typeof r.text === 'string' &&
    r.text.trim() !== '' &&
    Number.isInteger(r.rating) &&
    r.rating >= 1 &&
    r.rating <= 5 &&
    typeof r.date === 'string' &&
    /^\d{4}-\d{2}-\d{2}/.test(r.date) &&
    typeof r.url === 'string' &&
    r.url.startsWith('https://') &&
    typeof r.integration === 'string' &&
    r.integration !== '' &&
    r.customer !== null &&
    typeof r.customer === 'object' &&
    typeof r.customer.name === 'string';

  // "antonella" / "PETRA" → "Antonella" / "Petra"; mixed case ("HvP", "McKay") is kept as typed
  const capitalise = (word) => {
    const lower = word.toLowerCase();
    if (word !== lower && word !== word.toUpperCase()) return word;
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  // "Mr Stephen Cox" → "Stephen C.", "Petra von Bassewitz" → "Petra B.", "Riccardo" → "Riccardo"
  const shortName = (full) => {
    const parts = full.trim().split(/\s+/).filter((part) => part !== '' && !HONORIFIC.test(part));
    if (parts.length === 0) return '';
    const first = capitalise(parts[0]);
    if (parts.length === 1) return first;
    return `${first} ${parts.at(-1)[0].toUpperCase()}.`;
  };

  // Closing bracket of the array opening at `open`, skipping brackets inside string literals.
  const arrayEnd = (source, open) => {
    let depth = 0;
    let inString = false;
    for (let i = open; i < source.length; i++) {
      const c = source[i];
      if (inString) {
        if (c === '\\') i++;
        else if (c === '"') inString = false;
      } else if (c === '"') inString = true;
      else if (c === '[' || c === '{') depth++;
      else if ((c === ']' || c === '}') && --depth === 0) return i;
    }
    return -1;
  };

  customElements.define(
    'lab-reviews',
    class LabReviews extends HTMLElement {
      /* The script holds several `[{"id":…}]` arrays (colours, then the reviews, then each
         review's media). The first one whose entries are reviews is the list. A candidate that
         is not JSON is simply not the list, so its parse error is expected and skipped. */
      static reviewsFrom(source) {
        for (let open = source.indexOf('[{"id":"'); open !== -1; open = source.indexOf('[{"id":"', open + 1)) {
          const end = arrayEnd(source, open);
          if (end === -1) break;
          let items;
          try {
            items = JSON.parse(source.slice(open, end + 1));
          } catch {
            continue;
          }
          const reviews = items.filter(isReview);
          if (reviews.length > 0) return reviews;
        }
        throw new Error('lab-reviews: no review list in the Senja script (format changed?)');
      }

      static shortName = shortName;

      connectedCallback() {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load().catch((error) => console.error('lab-reviews: reviews not shown', error));
          },
          { rootMargin: '800px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer.disconnect();
        this.controller?.abort();
      }

      async load() {
        /* Senja's Cloudflare edge keeps a separate copy for cross-origin requests and ignores
           its own max-age=1 there: on 2026-09-30 it served a 25-minute-old file, from before a
           review sync, to the site while direct requests got the new one. The query string is
           part of the edge cache key, so a value that changes every 10 minutes caps that
           staleness at 10 minutes and still lets visitors in the same window share one copy. */
        const src = new URL(this.dataset.src);
        src.searchParams.set('w', String(Math.floor(Date.now() / REFRESH_MS)));
        const response = await fetch(src, {
          credentials: 'omit',
          signal: AbortSignal.timeout(TIMEOUT_MS),
        });
        if (!response.ok) throw new Error(`lab-reviews: Senja answered ${response.status}`);

        const limit = Number(this.dataset.limit) || 12;
        const minRating = Number(this.dataset.minRating) || 1;
        // Senja writes every date as "YYYY-MM-DD hh:mm:ss…+00", so the strings sort as dates
        const reviews = LabReviews.reviewsFrom(await response.text())
          .filter((review) => review.rating >= minRating)
          .sort((a, b) => b.date.localeCompare(a.date))
          .slice(0, limit);
        // Removed while the request was in flight (an editor section reload): binding the
        // arrows now would leave a window resize listener that disconnectedCallback never aborts.
        if (!this.isConnected) return;

        const day = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        });
        const template = this.querySelector('template').content.firstElementChild;
        const track = this.querySelector('[data-reviews-track]');

        track.replaceChildren(
          ...reviews.map((review) => {
            const card = template.cloneNode(true);
            card.dataset.source = review.integration;
            const stars = card.querySelector('[data-stars]');
            stars.dataset.rating = String(review.rating);
            stars.setAttribute('aria-label', `${review.rating} / 5`);
            stars.querySelectorAll('.rv-star').forEach((star, i) => {
              star.style.setProperty('--fill', i < review.rating ? '100%' : '0%');
            });
            card.querySelector('[data-name]').textContent = shortName(review.customer.name);
            card.querySelector('[data-date]').textContent = day.format(new Date(review.date.slice(0, 10)));
            card.querySelector('[data-date]').dateTime = review.date.slice(0, 10);
            card.querySelector('[data-source]').href = review.url;
            card.querySelectorAll('[data-logo]').forEach((logo) => {
              logo.hidden = logo.dataset.logo !== review.integration;
            });
            const text = card.querySelector('[data-text]');
            text.textContent = review.text;
            text.cite = review.url;
            if (typeof review.lang === 'string' && review.lang !== '') text.lang = review.lang;
            return card;
          })
        );
        this.querySelector('[data-reviews-body]').hidden = false;
        this.bindArrows(track);
      }

      bindArrows(track) {
        this.controller?.abort();
        this.controller = new AbortController();
        const { signal } = this.controller;
        const prev = this.querySelector('[data-reviews-prev]');
        const next = this.querySelector('[data-reviews-next]');
        const update = () => {
          const atStart = track.scrollLeft <= 1;
          const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
          // Move focus off an arrow before disabling it, or focus drops to <body>.
          if (atStart && atEnd) {
            // No overflow: both arrows get disabled, so hand focus to the track instead.
            if (document.activeElement === prev || document.activeElement === next) {
              track.tabIndex = -1;
              track.focus({ preventScroll: true });
            }
          } else {
            if (atEnd && document.activeElement === next) prev.focus({ preventScroll: true });
            if (atStart && document.activeElement === prev) next.focus({ preventScroll: true });
          }
          prev.disabled = atStart;
          next.disabled = atEnd;
        };
        const step = (direction) => {
          const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
          track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: smooth ? 'smooth' : 'auto' });
        };
        prev.addEventListener('click', () => step(-1), { signal });
        next.addEventListener('click', () => step(1), { signal });
        track.addEventListener('scroll', update, { passive: true, signal });
        window.addEventListener('resize', update, { passive: true, signal });
        update();
      }
    }
  );
}
